import {
  doc,
  getDoc,
  runTransaction,
  updateDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../../../lib/firebase"
import {
  generateRoomCode,
  shuffle,
  computePayouts,
  PLAYER_COLORS,
} from "../../../utils/ballparkLogic"
import { getQuestions } from "../../../data/ballparkQuestions"

const ID_KEY = "ballpark_player_id"
const NAME_KEY = "ballpark_player_name"

export const roomRef = (code) => doc(db, "ballpark_rooms", code.toUpperCase())

export function getIdentity() {
  let playerId = localStorage.getItem(ID_KEY)
  if (!playerId) {
    playerId = Math.random().toString(36).slice(2, 10)
    localStorage.setItem(ID_KEY, playerId)
  }
  return { playerId, name: localStorage.getItem(NAME_KEY) || "" }
}

export function saveIdentity(name) {
  localStorage.setItem(NAME_KEY, name)
}

const twelveHours = () => new Date(Date.now() + 12 * 60 * 60 * 1000)

function playerEntry(name, color) {
  return { name, color, score: 0, joinedAt: Date.now() }
}

export async function createRoom(hostName) {
  const { playerId } = getIdentity()
  saveIdentity(hostName)
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateRoomCode()
    const created = await runTransaction(db, async (tx) => {
      const snap = await tx.get(roomRef(code))
      if (snap.exists()) return false
      tx.set(roomRef(code), {
        phase: "lobby",
        round: 0,
        totalRounds: 7,
        packIds: ["general"],
        questionOrder: [],
        questionIndex: 0,
        hostId: playerId,
        createdAt: serverTimestamp(),
        expireAt: twelveHours(),
        players: { [playerId]: playerEntry(hostName, PLAYER_COLORS[0]) },
        guesses: {},
        bets: {},
        betsDone: {},
        lastResult: null,
        nextRoom: null,
        history: [],
      })
      return true
    })
    if (created) return code
  }
  throw new Error("could-not-create")
}

export async function joinRoom(code, name) {
  const { playerId } = getIdentity()
  saveIdentity(name)
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists()) throw new Error("not-found")
    const players = snap.data().players || {}
    if (players[playerId]) {
      tx.update(roomRef(code), { [`players.${playerId}.name`]: name })
      return
    }
    const color = PLAYER_COLORS[Object.keys(players).length % PLAYER_COLORS.length]
    tx.update(roomRef(code), {
      [`players.${playerId}`]: playerEntry(name, color),
    })
  })
}

export async function updateRoomSettings(code, settings) {
  await updateDoc(roomRef(code), settings)
}

export async function startGame(code, { totalRounds, packIds }) {
  const questions = getQuestions(packIds)
  const order = shuffle(questions.map((_, i) => i))
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "lobby") return
    tx.update(roomRef(code), {
      phase: "guessing",
      round: 1,
      totalRounds,
      packIds,
      questionOrder: order,
      questionIndex: 0,
      guesses: {},
      bets: {},
      betsDone: {},
      lastResult: null,
      history: [],
    })
  })
}

export async function submitGuess(code, playerId, value) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "guessing") return
    tx.update(roomRef(code), { [`guesses.${playerId}`]: value })
  })
}

export async function submitBets(code, playerId, bets) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "betting") return
    tx.update(roomRef(code), {
      [`bets.${playerId}`]: bets,
      [`betsDone.${playerId}`]: true,
    })
  })
}

// Phase transitions are transactions guarded on the current phase so that
// several clients racing to advance apply the change exactly once.
export async function endGuessing(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "guessing") return
    tx.update(roomRef(code), { phase: "betting" })
  })
}

export async function endBetting(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "betting") return
    const room = snap.data()
    const questions = getQuestions(room.packIds)
    const q = questions[room.questionOrder[room.questionIndex]]
    const result = computePayouts(
      { guesses: room.guesses, bets: room.bets },
      q.answer
    )
    const players = { ...room.players }
    Object.entries(result.deltas).forEach(([pid, delta]) => {
      if (players[pid]) {
        players[pid] = { ...players[pid], score: players[pid].score + delta }
      }
    })
    const roundResult = {
      round: room.round,
      question: q.question,
      answer: q.answer,
      note: q.note,
      winning: result.winning,
      winOdds: result.winOdds,
      winnerIds: result.winnerIds,
      deltas: result.deltas,
      slots: result.slots,
      guesses: room.guesses,
      bets: room.bets,
    }
    const history = [...(room.history || []), roundResult]
    tx.update(roomRef(code), {
      phase: "reveal",
      players,
      lastResult: roundResult,
      history,
    })
  })
}

export async function nextRound(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "reveal") return
    const room = snap.data()
    if (room.round >= room.totalRounds) {
      tx.update(roomRef(code), { phase: "gameover" })
      return
    }
    const total = getQuestions(room.packIds).length
    tx.update(roomRef(code), {
      phase: "guessing",
      round: room.round + 1,
      questionIndex: (room.questionIndex + 1) % total,
      guesses: {},
      bets: {},
      betsDone: {},
    })
  })
}

export async function skipQuestion(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "guessing") return
    const room = snap.data()
    const total = getQuestions(room.packIds).length
    tx.update(roomRef(code), {
      questionIndex: (room.questionIndex + 1) % total,
      guesses: {},
    })
  })
}

export async function endGame(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase === "gameover") return
    tx.update(roomRef(code), { phase: "gameover" })
  })
}

export async function playAgain(code, hostName) {
  const { playerId: hostId } = getIdentity()
  const oldSnap = await getDoc(roomRef(code))
  const oldPlayers = oldSnap.exists() ? oldSnap.data().players || {} : {}
  const newCode = await createRoom(hostName)
  const carried = {}
  Object.entries(oldPlayers).forEach(([pid, player]) => {
    if (pid === hostId) return
    carried[`players.${pid}`] = {
      name: player.name,
      color: player.color,
      joinedAt: player.joinedAt,
      score: 0,
    }
  })
  if (Object.keys(carried).length > 0) {
    await updateDoc(roomRef(newCode), carried)
  }
  await updateDoc(roomRef(code), { nextRoom: newCode })
  return newCode
}

export function subscribeRoom(code, onData, onError) {
  return onSnapshot(
    roomRef(code),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  )
}
